import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiErr, mediaUrl } from "@/lib/api";
import { MicButton } from "@/components/MicButton";
import { useAuth } from "@/context/AuthContext";
import { Container, PageHeader } from "@/components/shared";
import FamilyLink from "@/components/FamilyLink";
import PrivacyNote from "@/components/PrivacyNote";
import { PROFILES } from "@/components/ProfilePicker";
import { toast } from "sonner";
import { Crown, Trophy, Swords, Bookmark, Bell, Award, LayoutDashboard, History as HistoryIcon, Sparkles, UserCog, Check, Mail, Save, ShieldCheck, Download, Trash2, Loader2, AlertTriangle, Camera, Eye, Lock } from "lucide-react";

const SENDER_FIELDS = [
  { key: "prenom", label: "Prénom" },
  { key: "nom", label: "Nom" },
  { key: "adresse", label: "Adresse", full: true },
  { key: "code_postal", label: "Code postal" },
  { key: "ville", label: "Ville" },
  { key: "email", label: "Email" },
  { key: "telephone", label: "Téléphone (optionnel)" },
];

const BADGE_LABELS = {
  premier_debat: "Premier débat", cent_reponses: "100 réponses", mille_points: "1000 points",
  expert: "Expert", historien: "Historien", maitre_argumentation: "Maître de l'argumentation",
};
const ALL_BADGES = Object.keys(BADGE_LABELS);

export default function Profile() {
  const { user, refresh, setUser } = useAuth();
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [appInfo, setAppInfo] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [sender, setSender] = useState({});
  const [savingSender, setSavingSender] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [delPassword, setDelPassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [bio, setBio] = useState("");
  const [avatar, setAvatar] = useState("");
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [savingPublic, setSavingPublic] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  const changePassword = async () => {
    if (pw.next.length < 8) return toast.error("Le nouveau mot de passe doit faire au moins 8 caractères.");
    if (pw.next !== pw.confirm) return toast.error("Les deux mots de passe ne correspondent pas.");
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Mot de passe mis à jour.");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingPw(false); }
  };

  useEffect(() => { setBio(user?.bio || ""); setAvatar(user?.avatar || ""); }, [user?.bio, user?.avatar]);

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/afromap/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAvatar(data.path);
      await api.put("/me/public-profile", { avatar: data.path });
      await refresh();
      toast.success("Photo de profil mise à jour.");
    } catch (err) { toast.error(apiErr(err)); } finally { setUploadingAvatar(false); }
  };

  const savePublic = async () => {
    setSavingPublic(true);
    try {
      await api.put("/me/public-profile", { bio });
      await refresh();
      toast.success("Profil public enregistré.");
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingPublic(false); }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `bizarre-ai-mes-donnees.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Vos données ont été exportées.");
    } catch { toast.error("Export impossible pour le moment."); }
    finally { setExporting(false); }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/me", { data: { password: delPassword || null } });
      localStorage.removeItem("bizarre_token");
      setUser(false);
      toast.success("Votre compte et vos données ont été supprimés.");
      navigate("/login", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible.");
    } finally { setDeleting(false); }
  };

  useEffect(() => { if (user?.sender_info) setSender(user.sender_info); }, [user?.sender_info]);

  const saveSender = async () => {
    setSavingSender(true);
    try {
      await api.put("/me/sender-info", {
        nom: sender.nom || "", prenom: sender.prenom || "", adresse: sender.adresse || "",
        code_postal: sender.code_postal || "", ville: sender.ville || "",
        email: sender.email || "", telephone: sender.telephone || "",
      });
      await refresh();
      toast.success("Coordonnées enregistrées.");
    } catch { toast.error("Impossible d'enregistrer les coordonnées."); }
    finally { setSavingSender(false); }
  };

  const changeProfile = async (p) => {
    if (savingProfile || user?.profile === p) return;
    setSavingProfile(true);
    try {
      await api.put("/me/profile", { profile: p });
      await refresh();
      toast.success("Profil mis à jour. L'application s'adapte à vous.");
    } catch {
      toast.error("Impossible de modifier le profil.");
    } finally {
      setSavingProfile(false);
    }
  };

  useEffect(() => {
    refresh();
    api.get("/notifications").then(({ data }) => setNotes(data.notifications || [])).catch(() => {});
    api.get("/app/version").then(({ data }) => setAppInfo(data)).catch(() => {});
  }, []); // eslint-disable-line

  if (!user) return null;
  const nextLevel = user.level * 100;
  const prog = Math.min(100, Math.round(((user.points % 100) / 100) * 100));

  const stats = [
    { label: "Niveau", value: user.level, icon: Trophy },
    { label: "Points", value: user.points, icon: Award },
    { label: "Débats", value: user.debates_count, icon: Swords },
    { label: "Sauvegardés", value: user.saved_count, icon: Bookmark },
  ];

  return (
    <Container>
      <PageHeader eyebrow="Profil" title={user.name} />

      <div className="flex flex-wrap gap-3 mb-6">
        <Link to="/dashboard" data-testid="profile-dashboard-link"
          className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:border-gold/40 hover:text-gold transition-colors">
          <LayoutDashboard size={16} /> Tableau de bord
        </Link>
        <Link to="/history" data-testid="profile-history-link"
          className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:border-gold/40 hover:text-gold transition-colors">
          <HistoryIcon size={16} /> Historique & favoris
        </Link>
        <Link to="/mes-travaux" data-testid="profile-mywork-link"
          className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:border-gold/40 hover:text-gold transition-colors">
          <HistoryIcon size={16} /> Mes travaux
        </Link>
      </div>

      <PrivacyNote className="mb-6" />
      <div className="mb-6"><FamilyLink /></div>

      <div className="card-surface p-6 mb-6">
        <div className="flex items-center gap-4 mb-6">
          {mediaUrl(user.avatar) ? (
            <img src={mediaUrl(user.avatar)} alt={user.name} className="w-16 h-16 rounded-2xl object-cover border border-gold/25" />
          ) : (
            <div className="w-16 h-16 rounded-2xl gold-gradient flex items-center justify-center text-black font-display text-2xl">
              {user.name?.[0]?.toUpperCase()}
            </div>
          )}
          <div>
            <p className="font-display text-xl">{user.email}</p>
            <span className={`inline-flex items-center gap-1 text-xs mt-1 px-2 py-0.5 rounded-full ${user.is_premium ? "bg-gold/15 text-gold border border-gold/30" : "bg-white/5 text-white/50"}`}>
              {user.plan === "pro" ? <><Crown size={12} /> Pro</> : user.plan === "premium" ? <><Crown size={12} /> Premium</> : "Gratuit"}
            </span>
          </div>
        </div>
        <div className="mb-2 flex justify-between text-sm"><span className="text-white/50">Progression niveau {user.level}</span><span className="text-gold">{user.points} / {nextLevel} pts</span></div>
        <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full gold-gradient" style={{ width: `${prog}%` }} /></div>
      </div>

      {/* ---------------- PROFIL PUBLIC ---------------- */}
      <div className="card-surface p-6 mb-6" data-testid="public-profile-card">
        <div className="flex items-center gap-2 mb-1">
          <Eye size={18} className="text-gold" />
          <h3 className="font-display text-lg">Profil public</h3>
        </div>
        <p className="text-white/50 text-sm mb-4">Votre photo, votre présentation et vos badges sont visibles par les autres membres depuis la Carte Afro et l'Espace Communautaire.</p>
        <div className="flex items-center gap-4 mb-4">
          <div className="relative">
            {mediaUrl(avatar) ? (
              <img src={mediaUrl(avatar)} alt="" className="w-20 h-20 rounded-full object-cover border border-gold/30" />
            ) : (
              <div className="w-20 h-20 rounded-full gold-gradient flex items-center justify-center text-black font-display text-3xl">{user.name?.[0]?.toUpperCase()}</div>
            )}
            <label className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#0A0A0A] border border-gold/40 flex items-center justify-center cursor-pointer hover:bg-gold/10 transition-colors" title="Changer la photo">
              {uploadingAvatar ? <Loader2 size={14} className="animate-spin text-gold" /> : <Camera size={14} className="text-gold" />}
              <input data-testid="avatar-upload" type="file" accept="image/*" className="hidden" onChange={uploadAvatar} disabled={uploadingAvatar} />
            </label>
          </div>
          <div className="flex-1">
            <label className="text-white/50 text-xs block mb-1">Bio / présentation</label>
            <textarea data-testid="bio-input" value={bio} maxLength={500} rows={3} onChange={(e) => setBio(e.target.value)}
              placeholder="Quelques mots sur vous, vos centres d'intérêt, votre activité…"
              className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm resize-none focus:border-gold/40" />
            <div className="flex items-center justify-between mt-1">
              <MicButton testid="bio-mic" value={bio} onChange={(v) => setBio(v.slice(0, 500))} />
              <p className="text-white/30 text-[11px] text-right">{bio.length}/500</p>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button data-testid="save-public-profile" onClick={savePublic} disabled={savingPublic}
            className="gold-gradient text-black rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
            {savingPublic ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Enregistrer
          </button>
          <Link to={`/membre/${user.user_id}`} data-testid="view-public-profile"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm hover:border-gold/40 hover:text-gold transition-colors">
            <Eye size={15} /> Voir mon profil public
          </Link>
        </div>
      </div>

      <div className="card-surface p-6 mb-6" data-testid="profile-selector">
        <div className="flex items-center gap-2 mb-1">
          <UserCog size={18} className="text-gold" />
          <h3 className="font-display text-lg">Mon profil</h3>
        </div>
        <p className="text-white/50 text-sm mb-4">Changez de profil à tout moment. Le vocabulaire, les exemples, les fiches et les conseils de l'IA s'adaptent immédiatement.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {PROFILES.map((p) => {
            const active = user.profile === p.key;
            return (
              <button key={p.key} data-testid={`profile-option-${p.key}`} disabled={savingProfile}
                onClick={() => changeProfile(p.key)}
                className={`relative text-left rounded-2xl border p-4 transition-colors disabled:opacity-50 ${active ? "border-gold/50 bg-gold/[0.06]" : "border-white/10 hover:border-gold/30"}`}>
                {active && <span className="absolute top-3 right-3 text-gold"><Check size={18} /></span>}
                <span className="text-2xl">{p.emoji}</span>
                <p className="font-display text-base mt-2">{p.label} <span className="text-white/40 text-xs font-normal">· {p.hint}</span></p>
                <p className="text-white/50 text-sm mt-0.5">{p.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="card-surface p-6 mb-6" data-testid="sender-info-card">
        <div className="flex items-center gap-2 mb-1">
          <Mail size={18} className="text-gold" />
          <h3 className="font-display text-lg">Mes coordonnées</h3>
        </div>
        <p className="text-white/50 text-sm mb-4">Ces informations apparaissent automatiquement en haut de vos courriers PDF et dans la signature.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {SENDER_FIELDS.map((f) => (
            <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
              <label className="text-white/50 text-xs block mb-1">{f.label}</label>
              <input data-testid={`sender-${f.key}`} value={sender[f.key] || ""}
                onChange={(e) => setSender((s) => ({ ...s, [f.key]: e.target.value }))}
                className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm" />
            </div>
          ))}
        </div>
        <button data-testid="sender-save" onClick={saveSender} disabled={savingSender}
          className="mt-4 gold-gradient text-black rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
          <Save size={15} /> {savingSender ? "Enregistrement…" : "Enregistrer mes coordonnées"}
        </button>
      </div>

      <div className="card-surface p-6 mb-6" data-testid="change-password-card">
        <div className="flex items-center gap-2 mb-1">
          <Lock size={18} className="text-gold" />
          <h3 className="font-display text-lg">Mot de passe</h3>
        </div>
        <p className="text-white/50 text-sm mb-4">
          {user?.password_hash === false
            ? "Votre compte utilise Google. Vous pouvez définir un mot de passe pour vous connecter aussi par e-mail."
            : "Choisissez votre propre mot de passe. Il reste valable partout, sur mobile comme sur ordinateur."}
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          {user?.password_hash !== false && (
            <input data-testid="pw-current" type="password" placeholder="Mot de passe actuel" value={pw.current}
              onChange={(e) => setPw((s) => ({ ...s, current: e.target.value }))}
              className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm" />
          )}
          <input data-testid="pw-new" type="password" placeholder="Nouveau mot de passe" value={pw.next}
            onChange={(e) => setPw((s) => ({ ...s, next: e.target.value }))}
            className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm" />
          <input data-testid="pw-confirm" type="password" placeholder="Confirmer" value={pw.confirm}
            onChange={(e) => setPw((s) => ({ ...s, confirm: e.target.value }))}
            className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm" />
        </div>
        <button data-testid="pw-save" onClick={changePassword} disabled={savingPw}
          className="mt-4 gold-gradient text-black rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
          {savingPw ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} {savingPw ? "Enregistrement…" : "Mettre à jour le mot de passe"}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} data-testid={`stat-${s.label}`} className="card-surface p-5 text-center">
              <Icon size={20} className="text-gold mx-auto mb-2" strokeWidth={1.5} />
              <div className="font-display text-2xl">{s.value}</div>
              <div className="text-white/40 text-xs mt-1">{s.label}</div>
            </div>
          );
        })}
      </div>

      <h3 className="font-display text-xl mb-3">Badges</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        {ALL_BADGES.map((b) => {
          const earned = user.badges?.includes(b);
          return (
            <div key={b} data-testid={`badge-${b}`} className={`card-surface p-5 flex items-center gap-3 ${earned ? "border-gold/40" : "opacity-40"}`}>
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${earned ? "gold-gradient" : "bg-white/5"}`}>
                <Award size={20} className={earned ? "text-black" : "text-white/40"} />
              </div>
              <div>
                <p className="text-sm font-medium">{BADGE_LABELS[b]}</p>
                <p className="text-white/40 text-xs">{earned ? "Débloqué" : "Verrouillé"}</p>
              </div>
            </div>
          );
        })}
      </div>

      <h3 className="font-display text-xl mb-3 flex items-center gap-2"><Bell size={18} className="text-gold" /> Notifications</h3>
      <div className="space-y-2">
        {notes.map((n) => (
          <div key={n.notification_id} className="card-surface p-4">
            <p className="text-sm font-medium">{n.title}</p>
            <p className="text-white/50 text-sm">{n.body}</p>
          </div>
        ))}
      </div>

      {appInfo && (
        <>
          <h3 className="font-display text-xl mt-8 mb-3 flex items-center gap-2">
            <Sparkles size={18} className="text-gold" /> Nouveautés
            <span className="text-white/40 text-sm font-normal">· version {appInfo.version}</span>
          </h3>
          <div className="space-y-3">
            {appInfo.changelog.map((c) => (
              <div key={c.version} data-testid={`changelog-${c.version}`} className="card-surface p-4">
                <p className="text-gold text-xs uppercase tracking-[0.15em] mb-2">v{c.version} · {c.date}</p>
                <ul className="space-y-1">
                  {c.changes.map((ch, i) => (
                    <li key={i} className="text-white/70 text-sm flex gap-2"><span className="text-gold/60">—</span> {ch}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </>
      )}

      {!user.is_premium && (
        <Link to="/premium" className="mt-6 block text-center gold-gradient text-black rounded-full py-3 font-medium">
          Passer Premium — 9,99 €/mois
        </Link>
      )}

      {user.is_premium && !user.subscription_id && (
        <div data-testid="profile-beta-access" className="mt-6 w-full text-center rounded-full border border-gold/30 text-gold/80 py-3 text-sm">
          Accès Premium offert (bêta) — aucun abonnement à gérer.
        </div>
      )}

      {user.is_premium && user.subscription_id && (
        <button data-testid="profile-manage-subscription" onClick={async () => {
          try { const { data } = await api.post("/payments/portal", { origin_url: window.location.origin }); window.location.href = data.url; }
          catch (e) { toast.error(apiErr(e)); }
        }}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 text-gold py-3 font-medium hover:bg-gold/10 transition-colors">
          <Crown size={16} /> Gérer mon abonnement
        </button>
      )}

      {/* ---------------- CONFIDENTIALITÉ & DONNÉES (RGPD) ---------------- */}
      <div className="card-surface p-6 mt-8" data-testid="privacy-data-card">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={18} className="text-gold" />
          <h3 className="font-display text-lg">Confidentialité & mes données</h3>
        </div>
        <p className="text-white/50 text-sm mb-4">Conformément au RGPD, vous pouvez exporter vos données ou supprimer définitivement votre compte à tout moment.</p>
        <div className="flex flex-wrap gap-3 mb-5">
          <button data-testid="export-data-btn" onClick={exportData} disabled={exporting}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:border-gold/40 hover:text-gold transition-colors disabled:opacity-50">
            {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Exporter mes données
          </button>
          <Link to="/privacy" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-white/60 hover:text-gold hover:border-gold/40 transition-colors">Confidentialité</Link>
          <Link to="/cgu" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-white/60 hover:text-gold hover:border-gold/40 transition-colors">CGU / CGV</Link>
          <Link to="/mentions-legales" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-white/60 hover:text-gold hover:border-gold/40 transition-colors">Mentions légales</Link>
        </div>
        <div className="rounded-2xl border border-red-500/25 bg-red-500/[0.04] p-4">
          <p className="text-sm text-white/80 flex items-center gap-2 mb-1"><AlertTriangle size={15} className="text-red-400" /> Zone sensible</p>
          <p className="text-white/45 text-sm mb-3">La suppression est <span className="text-white/80">définitive</span> : votre compte et toutes vos données (historique, courriers, statistiques) seront effacés.</p>
          <button data-testid="delete-account-btn" onClick={() => setShowDelete(true)}
            className="inline-flex items-center gap-2 rounded-full border border-red-500/40 text-red-300 px-4 py-2 text-sm hover:bg-red-500/10 transition-colors">
            <Trash2 size={15} /> Supprimer mon compte
          </button>
        </div>
      </div>

      {showDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)" }}>
          <div className="w-full max-w-md card-surface p-7 border-red-500/30" data-testid="delete-account-modal">
            <div className="w-12 h-12 rounded-2xl bg-red-500/15 border border-red-500/30 flex items-center justify-center mb-4">
              <Trash2 size={22} className="text-red-400" />
            </div>
            <h2 className="font-display text-xl mb-2">Supprimer définitivement votre compte ?</h2>
            <p className="text-white/55 text-sm mb-4">Cette action est irréversible. Toutes vos données seront effacées immédiatement.</p>
            {user.password_hash !== false && (<input data-testid="delete-password-input" type="password" value={delPassword}
                onChange={(e) => setDelPassword(e.target.value)} placeholder="Mot de passe (requis pour les comptes e-mail)"
                className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm mb-4 placeholder:text-white/30" />
            )}
            <div className="flex gap-3">
              <button data-testid="delete-cancel-btn" onClick={() => { setShowDelete(false); setDelPassword(""); }}
                className="flex-1 rounded-full border border-white/15 py-2.5 text-sm hover:bg-white/[0.06] transition-colors">Annuler</button>
              <button data-testid="delete-confirm-btn" onClick={deleteAccount} disabled={deleting}
                className="flex-1 rounded-full bg-red-500/90 text-white py-2.5 text-sm font-medium hover:bg-red-500 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
}
